// Module-level cache for admin landing-copy overrides.
// Fetched once per page load; hook subscribes to updates.
// Same design pattern as useImageOverrides so Welcome.jsx never blocks on it
// and always falls back to the hardcoded defaults in /src/config/landing.js.
import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../App";

let _cache = null;      // { headline: {en, it}, sub: {en, it}, cta: {en, it} }
let _promise = null;    // in-flight fetch
const _listeners = new Set();

async function _load() {
  if (_cache) return _cache;
  if (_promise) return _promise;
  _promise = axios.get(`${API}/landing-copy`)
    .then((r) => {
      _cache = r.data?.copy || {};
      return _cache;
    })
    .catch(() => {
      _cache = {};
      return _cache;
    })
    .finally(() => { _promise = null; });
  return _promise;
}

export function useLandingCopy() {
  const [copy, setCopy] = useState(_cache || {});
  useEffect(() => {
    let alive = true;
    _load().then((m) => { if (alive) setCopy(m); });
    const on = (m) => { if (alive) setCopy({ ...m }); };
    _listeners.add(on);
    return () => { alive = false; _listeners.delete(on); };
  }, []);
  return copy;
}

// Resolve one field: override wins if non-empty, otherwise return the default.
// `field` is one of "headline" | "sub" | "cta".
// `lang`  is "en" | "it".
// `fallbackLocalized` is the {en, it} object from landing.js (e.g. HERO.headline).
export function resolveCopy(copy, field, lang, fallbackLocalized) {
  const override = copy && copy[field] && copy[field][lang];
  if (typeof override === "string" && override.trim() !== "") return override;
  return fallbackLocalized ? fallbackLocalized[lang] : "";
}

// Force a refresh after admin saves.
export function refreshLandingCopy() {
  _cache = null;
  return _load().then((m) => { _listeners.forEach((cb) => cb(m)); return m; });
}
